import { useState, useEffect } from 'react';
import { Range, getTrackBackground } from 'react-range';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

// Breakpoint labels and values
const BREAKPOINTS = [5, 30, 50, 100, 150, null]; // null represents "Unlimited"
const MIN = 0;
const MAX = BREAKPOINTS.length - 1;

const breakpointLabels: { [key: number]: string } = {
  0: '5',
  1: '30',
  2: '50',
  3: '100',
  4: '150',
  5: 'Unlimited',
};

type CustomRangeSliderProps = {
  initialValue: number | null;
  onChange: (value: number | null) => void;
  /** Same palette as Job Title / Industry years sliders (theme-aware when passed from parent). */
  trackColors?: [string, string];
};

export default function CustomRangeSlider({
  initialValue,
  onChange,
  trackColors = ['#3dcbb1', 'rgba(41,41,41,0.1)'],
}: CustomRangeSliderProps) {
  const initialIndex = BREAKPOINTS.indexOf(initialValue);
  const [mileSliderValue, setMileSliderValue] = useState(initialIndex !== -1 ? initialIndex : 0); // index
  const [showThumbTooltip, setShowThumbTooltip] = useState(false); // Keep this state for hover behavior

  // Sync slider value when initialValue prop changes
  useEffect(() => {
    const newIndex = BREAKPOINTS.indexOf(initialValue);
    if (newIndex !== -1) {
      setMileSliderValue(newIndex);
    } else {
      // If value is not in breakpoints, default to index 0 (0 miles) to match initial state behavior
      setMileSliderValue(0);
    }
  }, [initialValue]);

  const handleMileSliderChange = (index: number) => {
    const value = BREAKPOINTS[index];
    onChange(value);
  };

  const renderThumbTooltip = (index: number) => (
    <Tooltip id="slider-tooltip" className='bs-tooltip'>
      {breakpointLabels[index]}
    </Tooltip>
  );

  return (
    <div className="mile-slider-wrapper custom-slider">
      <Range
        step={1}
        min={MIN}
        max={MAX}
        values={[mileSliderValue]}
        onChange={([uiValue]) => {
          setMileSliderValue(uiValue);
          handleMileSliderChange(uiValue);
        }}
        renderTrack={({ props, children }) => (
          <div
            {...props}
            className="mile-slider-track"
            style={{
              background: getTrackBackground({
                values: [mileSliderValue],
                colors: trackColors,
                min: MIN,
                max: MAX,
              }),
              width: '100%',
            }}
          >
            {children}
          </div>
        )}
        renderThumb={({ props }) => (
          <OverlayTrigger
            placement="bottom"
            overlay={renderThumbTooltip(mileSliderValue)}
            show={showThumbTooltip}
          >
            <div
              {...props}
              className="mile-slider-thumb"
              onMouseEnter={() => setShowThumbTooltip(true)}
              onMouseLeave={() => setShowThumbTooltip(false)}
            >
              <span className="mile-slider-thumb-value">
                {/* Optional display inside thumb */}
              </span>
            </div>
          </OverlayTrigger>
        )}
      />

      {/* Optional: Show label below the track */}
      <div className="mile-slider-labels">
        {Object.values(breakpointLabels).map((label, i) => (
          <span key={i} className="mile-slider-label">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}